import React, { useState, useRef, useEffect } from "react";
import { Bell, Check, ArrowLeftRight, Clock, RotateCcw, ChevronDown, AlertTriangle, ArrowUpRight, Wrench, AlertOctagon, FileQuestion, CalendarX, TrendingDown, TrendingUp, Trophy, MessageSquare, ListChecks, Eye, Copy, PanelLeftClose, PanelLeftOpen, LogOut, UserCog } from "lucide-react";
import { titleFor } from "../lib/menu.js";
import FeedbackModal from "../components/FeedbackModal.jsx";
import NapovedaTrenera from "../components/NapovedaTrenera.jsx";
import VyberTrenera from "../components/VyberTrenera.jsx";
import { useNahledTrenera } from "../lib/nahledTrenera.js";
import TrenerBell from "../components/TrenerBell.jsx";
import ManagerBell from "../components/ManagerBell.jsx";
import NotifPanel from "../components/NotifPanel.jsx";
import TimerWidget from "../components/TimerWidget.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import { useAlertCount, useNotifications } from "../lib/AlertContext.jsx";
import { getCurrentUser, getRealUser, getViewAsRole, setViewAsRole, defaultPathForRole, logout } from "../lib/auth.js";

import { UDALOST as UDALOST_ZALOZKA } from "../lib/taby.js";

// Které role mohou vidět vlastnické (manažerské) notifikace v zvonečku.
// Notifikace v /api/notifications jsou typu: undecided lead, missing invoice/reservio,
// ROI low, CPL high, milníky — to vše manažerské metriky. Trenér je nemá vidět.
const ROLES_S_NOTIFIKACEMI = new Set(["owner", "manager", "sales", "nutritionist"]);

// Mapování role → label pro badge
const ROLE_LABEL = {
  owner: "Owner",
  manager: "Manager",
  sales: "Sales",
  csm: "CSM",
  marketing: "Marketing",
  trener: "Trenér",
  bc_trener: "Trenér bootcampů",
  nutritionist: "Nutritionist",
};
// Barvy badge per role — vizuálně odlišit kdo je přihlášený
const ROLE_BADGE_STYLE = {
  owner:     "bg-amber-100 text-amber-800 border-amber-300",
  manager:   "bg-plum-100 text-plum-800 border-plum-300",
  sales:     "bg-emerald-100 text-emerald-800 border-emerald-300",
  csm:       "bg-sky-100 text-sky-800 border-sky-300",
  marketing: "bg-rose-100 text-rose-800 border-rose-300",
  trener:    "bg-brand-100 text-brand-800 border-brand-300",
  bc_trener: "bg-brand-200 text-brand-900 border-brand-400",
  nutritionist: "bg-lime-100 text-lime-800 border-lime-300",
};

// Název stránky se skládá z menu (src/lib/menu.js), takže ho má každá sekce,
// ne jen těch devět, které tu byly vypsané ručně.

const TYPE_META = {
  undecided:       { label: "Nerozhodnutí", text: "text-red-700",   bg: "bg-red-50",    border: "border-red-200",    Icon: AlertTriangle },
  missing_invoice: { label: "Chybí faktura", text: "text-amber-700", bg: "bg-amber-50",  border: "border-amber-200",  Icon: FileQuestion },
  missing_reservio:{ label: "Chybí termín",  text: "text-sky-700",   bg: "bg-sky-50",    border: "border-sky-200",    Icon: CalendarX },
  roi_low:         { label: "Nízké ROI",     text: "text-rose-700",  bg: "bg-rose-50",   border: "border-rose-200",   Icon: TrendingDown },
  cpl_high:        { label: "Vysoký CPL",    text: "text-orange-700",bg: "bg-orange-50", border: "border-orange-200", Icon: TrendingUp },
  milestone:       { label: "Milník",        text: "text-emerald-700",bg: "bg-emerald-50",border: "border-emerald-300", Icon: Trophy },
  // Nove hlaseni z formulare Zpetna vazba — bez vizitky padalo na "Nerozhodnuti" (cerveny error styl).
  feedback_new:    { label: "Zpětná vazba",  text: "text-brand-700", bg: "bg-brand-50", border: "border-brand-200", Icon: MessageSquare },
  // FW-BOTI (22. 10. 2026): token AI bota za 14 dní vyprší, owner ho prodlouží v Nastavení → AI boti.
  bot_token_vyprsi: { label: "Token AI bota", text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-300", Icon: Clock },
  bot_potvrzeni: { label: "Potvrzení pro Anetu", text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-300", Icon: Clock },
  // 21.9.2026: absence čekající na schválení ownerem (dřív padaly na červené „Nerozhodnutí“).
  absence_pending: { label: "Absence ke schválení", text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-300", Icon: Clock },
  firemni_volno_pending: { label: "Firemní volno ke schválení", text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-300", Icon: Clock },
  absence_trener:  { label: "Absence trenéra",  text: "text-sky-700",   bg: "bg-sky-50",   border: "border-sky-200",   Icon: CalendarX },
  // FW-158: @zmínky a osobní zprávy (např. vyřízené hlášení) dřív padaly na červené „Nerozhodnutí".
  zminka:          { label: "Zmínka v úkolu", text: "text-brand-700", bg: "bg-brand-50", border: "border-brand-200", Icon: MessageSquare },
  zprava:          { label: "Zpráva",         text: "text-brand-700", bg: "bg-brand-50", border: "border-brand-200", Icon: MessageSquare },
  // 25. 10. 2026: systémové chyby (např. meta-capi-test: HTTP 502) padaly na červené „Nerozhodnutí“.
  system_error_critical: { label: "Kritická chyba", text: "text-red-700",    bg: "bg-red-50",    border: "border-red-300",    Icon: AlertOctagon },
  system_error_error:    { label: "Chyba systému",  text: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200", Icon: Wrench },
  system_error_warn:     { label: "Varování systému", text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200",  Icon: Wrench },
};

// 25. 10. 2026 (Láďa): ze zmínky v úkolu jde rovnou otevřít úkol. Stejné odkazy jako Historie úkolů.
function odkazNaUkol(z) {
  if (!z || !z.ukol_id || z.tabulka === "zprava") return null;
  return z.tabulka === "trener"
    ? `/manager/treneri/ukoly?open=${encodeURIComponent(z.ukol_id)}`
    : `/operations/ukoly?ukol=${encodeURIComponent(z.ukol_id)}`;
}

// 25. 10. 2026 (Láďa, hlášení #178): přehlednější notifikace. Bílý seznam, barevná je jen ikona,
// u každé položky čas vzniku a hlavní akce jako výrazné tlačítko (otevřít úkol, absenci, hlášení,
// kontakt nebo detail). Vyřízeno a Odložit jsou stejně velká tlačítka, zaškrtávání až po „Vybrat více“.
const IKONA_TON = {
  "text-red-700": "bg-red-50 text-red-700", "text-amber-700": "bg-amber-50 text-amber-700", "text-sky-700": "bg-sky-50 text-sky-700",
  "text-rose-700": "bg-rose-50 text-rose-700", "text-orange-700": "bg-orange-50 text-orange-700", "text-emerald-700": "bg-emerald-50 text-emerald-700",
  "text-brand-700": "bg-brand-50 text-brand-700",
};
const metaNotifikace = (n) => (n.typ === "zminka" && n.zminka?.tabulka === "zprava" ? TYPE_META.zprava : TYPE_META[n.typ]) || TYPE_META.undecided;

// Kam vede hlavní tlačítko. Jen tam, kde odkaz opravdu existuje, jinak tlačítko není.
function hlavniAkce(n) {
  if (n.typ === "zminka") { const to = odkazNaUkol(n.zminka); return to ? { label: "Otevřít úkol", to } : null; }
  if (n.typ === "absence_pending" || n.typ === "absence_trener") return { label: "Otevřít absence", to: "/hr/absence" };
  if (n.typ === "firemni_volno_pending") return { label: "Otevřít firemní volno", to: "/clients/volno" };
  if (n.typ === "feedback_new") return { label: "Otevřít hlášení", to: "/feedback" };
  if (n.typ === "undecided" && n.klient) return { label: "Otevřít kontakt", to: `/sales/kontakty?q=${encodeURIComponent(n.klient)}` };
  if (String(n.typ || "").startsWith("system_error")) return { label: "Zobrazit detail", rozbalit: true };
  return null;
}

// "25.10.2026 05:10" (byznys) i ISO (zmínky, systémové chyby) na Date.
// Pražský posun (v minutách) pro daný okamžik, ať čas sedí i mimo Česko (audit 25. 10. 2026).
function prahaPosun(utcMs) {
  try {
    const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Prague", hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(new Date(utcMs)).map((x) => [x.type, x.value]));
    return (Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute) - utcMs) / 60000;
  } catch { return 60; }
}
function casVzniku(v) {
  if (!v) return null;
  const m = String(v).match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/);
  if (!m) { const d = new Date(v); return isNaN(d.getTime()) ? null : d; }
  // "D.M.YYYY HH:MM" je pražský čas.
  const jakoUtc = Date.UTC(+m[3], +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0));
  const d = new Date(jakoUtc - prahaPosun(jakoUtc) * 60000);
  return isNaN(d.getTime()) ? null : d;
}
// Kalendářní den v Praze jako číslo (pro dnes / včera).
const prahaDen = (d) => { const x = new Date(d.getTime() + prahaPosun(d.getTime()) * 60000); return Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate()) / 864e5; };
function kdyPopis(v) {
  const d = casVzniku(v); if (!d) return "";
  const min = Math.round((Date.now() - d.getTime()) / 60000);
  if (min < 2) return "teď";
  if (min < 60) return `před ${min} min`;
  const x = new Date(d.getTime() + prahaPosun(d.getTime()) * 60000);
  const hm = `${x.getUTCHours()}:${String(x.getUTCMinutes()).padStart(2, "0")}`;
  const rozdil = prahaDen(new Date()) - prahaDen(d);
  if (rozdil === 0) return `dnes ${hm}`;
  if (rozdil === 1) return `včera ${hm}`;
  return `${x.getUTCDate()}. ${x.getUTCMonth() + 1}.`;
}

const BTN_NOTIF = "inline-flex min-h-[44px] sm:min-h-[36px] items-center justify-center gap-1.5 rounded-full px-3.5 sm:px-3 text-[14px] sm:text-[13px] font-semibold active:scale-[0.98] transition";
const BTN_NOTIF_OBRYS = `${BTN_NOTIF} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`;

function OdlozitMenu({ onVyber, nahoru = true, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`relative ${className}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className={`${BTN_NOTIF_OBRYS} w-full`}>
        <Clock className="h-4 w-4" /> Odložit <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className={`absolute right-0 ${nahoru ? "bottom-full mb-1" : "top-full mt-1"} z-30 min-w-[120px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg`}>
          {[{ d: 3, l: "3 dny" }, { d: 7, l: "7 dní" }, { d: 30, l: "30 dní" }].map((o) => (
            <button key={o.d} type="button" onClick={() => { setOpen(false); onVyber(o.d); }}
              className="block w-full px-4 py-2.5 text-left text-[14px] hover:bg-slate-50 sm:py-2 sm:text-[13px]">{o.l}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniNotificationItem({ n, onArchive, onSnooze, onRestore, selected, onToggleSelect, vyber, onOtevrit, tab = "aktivní" }) {
  const meta = metaNotifikace(n);
  const [rozbaleno, setRozbaleno] = useState(false);
  const akce = tab === "aktivní" ? hlavniAkce(n) : null;
  const kdy = kdyPopis(n.vytvoreno);
  const ton = IKONA_TON[meta.text] || "bg-slate-100 text-slate-600";
  return (
    <div className={`border-b border-slate-100 px-4 py-3.5 last:border-b-0 sm:hover:bg-slate-50/70 ${selected ? "bg-brand-50/60" : ""}`}>
      <div className="flex gap-3">
        {vyber && (
          <input type="checkbox" checked={!!selected} onChange={() => onToggleSelect(n.id)} aria-label={`Vybrat ${n.klient || meta.label}`}
            className="mt-3 h-5 w-5 shrink-0 cursor-pointer accent-brand-600 sm:h-4 sm:w-4" />
        )}
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${ton}`}><meta.Icon className="h-[18px] w-[18px]" /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[11px] font-bold uppercase tracking-wider text-slate-500">{meta.label}</span>
            {kdy && <span className="ml-auto shrink-0 text-[12px] text-slate-400">{kdy}</span>}
          </div>
          <div className="mt-0.5 text-[16px] font-semibold leading-snug text-slate-900 sm:text-[15px] demo-sensitive">{n.klient}</div>
          {n.detail && <div className={`mt-0.5 whitespace-pre-line text-[14px] leading-snug text-slate-600 sm:text-[13px] ${rozbaleno ? "" : "line-clamp-2"}`}>{n.detail}</div>}
          {tab === "odloženo" && n.odlozeno_do && <div className="mt-1 text-[13px] text-slate-400">Vrátí se {n.odlozeno_do} v 9:00</div>}

          <div className={`mt-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center`}>
            {akce && (
              <button type="button"
                onClick={() => (akce.rozbalit ? setRozbaleno((r) => !r) : onOtevrit(n, akce.to))}
                className={`${BTN_NOTIF} col-span-2 bg-brand-600 text-white hover:bg-brand-700`}>
                <ArrowUpRight className="h-4 w-4" /> {akce.rozbalit && rozbaleno ? "Skrýt detail" : akce.label}
              </button>
            )}
            {tab === "aktivní" && (
              <>
                <button type="button" onClick={() => onArchive(n.id)} className={`${BTN_NOTIF_OBRYS} hover:border-emerald-300 hover:text-emerald-700`} title="Označit jako vyřízené">
                  <Check className="h-4 w-4" /> Vyřízeno
                </button>
                <OdlozitMenu onVyber={(d) => onSnooze(n.id, d)} />
              </>
            )}
            {tab !== "aktivní" && (
              <>
                <button type="button" onClick={() => onRestore(n.id)} className={`${BTN_NOTIF_OBRYS} ${tab === "odloženo" ? "" : "col-span-2"}`}>
                  <RotateCcw className="h-4 w-4" /> {tab === "odloženo" ? "Aktivovat teď" : "Obnovit"}
                </button>
                {tab === "odloženo" && (
                  <button type="button" onClick={() => onArchive(n.id)} className={`${BTN_NOTIF_OBRYS} hover:border-emerald-300 hover:text-emerald-700`}>
                    <Check className="h-4 w-4" /> Vyřízeno
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function BellDropdown({ onClose }) {
  const navigate = useNavigate();
  const { notifications, counts, archive, snooze, restore, bulkArchive, bulkSnooze } = useNotifications();
  const [tab, setTab] = useState("aktivní");
  const [vyber, setVyber] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const visible = notifications.filter((n) => n.stav === tab);
  const allSelected = visible.length > 0 && visible.every((n) => selectedIds.has(n.id));
  const someSelected = selectedIds.size > 0;
  const lzeVybirat = tab === "aktivní" || tab === "odloženo";

  const changeTab = (k) => { setTab(k); setSelectedIds(new Set()); setVyber(false); };
  const toggleSelect = (id) => setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(visible.map((n) => n.id)));
  const doBulkArchive = async () => { const ids = [...selectedIds]; setSelectedIds(new Set()); setVyber(false); await bulkArchive(ids); };
  const doBulkSnooze = async (days) => { const ids = [...selectedIds]; setSelectedIds(new Set()); setVyber(false); await bulkSnooze(ids, days); };
  const otevrit = (n, to) => { if (n.typ === "zminka") archive(n.id); onClose?.(); navigate(to); };

  const pocetText = (c) => (c === 1 ? "1 čeká na tebe" : c >= 2 && c <= 4 ? `${c} čekají na tebe` : `${c} čeká na tebe`);

  return (
    // Mobil: panel přes celou obrazovku (jako klientský portál), desktop: dropdown.
    <NotifPanel open onClose={onClose} count={counts.aktivní} iconClass="text-slate-700" widthClass="sm:w-[min(30rem,94vw)]">
      <div className="mx-4 mt-3 flex gap-1 rounded-full bg-slate-100 p-1" role="tablist">
        {[
          { key: "aktivní", label: "Aktivní", count: counts.aktivní },
          { key: "odloženo", label: "Odložené", count: counts.odloženo },
          { key: "archivováno", label: "Archiv", count: counts.archivováno },
        ].map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => changeTab(t.key)}
            className={`inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-full text-[14px] sm:min-h-[34px] sm:text-[13px] ${tab === t.key ? "bg-white font-semibold text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {t.label}
            {t.count > 0 && <span className={`rounded-full px-1.5 text-[11px] font-bold ${t.key === "aktivní" ? "bg-rose-500 text-white" : "bg-slate-300 text-slate-700"}`}>{t.count}</span>}
          </button>
        ))}
      </div>

      {visible.length > 0 && (
        <div className="flex min-h-[44px] flex-wrap items-center justify-between gap-2 px-4 pt-2 text-[13px]">
          {vyber ? (
            <label className="inline-flex cursor-pointer select-none items-center gap-2 text-slate-600">
              <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="h-5 w-5 accent-brand-600 sm:h-4 sm:w-4" />
              {someSelected ? `${selectedIds.size} vybráno` : "Vybrat vše"}
            </label>
          ) : (
            <span className="text-slate-500">{tab === "aktivní" ? pocetText(visible.length) : tab === "odloženo" ? `${visible.length} odložených` : `${visible.length} v archivu`}</span>
          )}
          {lzeVybirat && (
            <button type="button" onClick={() => { setVyber((v) => !v); setSelectedIds(new Set()); }}
              className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full px-2 font-semibold text-brand-700 hover:bg-brand-50">
              {vyber ? <><Check className="h-4 w-4" /> Hotovo</> : <><ListChecks className="h-4 w-4" /> Vybrat více</>}
            </button>
          )}
        </div>
      )}

      {vyber && someSelected && (
        <div className="mx-4 mb-1 grid grid-cols-2 gap-2">
          <button type="button" onClick={doBulkArchive} className={`${BTN_NOTIF_OBRYS} hover:border-emerald-300 hover:text-emerald-700`}><Check className="h-4 w-4" /> Vyřízeno ({selectedIds.size})</button>
          <OdlozitMenu onVyber={doBulkSnooze} nahoru={false} />
        </div>
      )}

      <div className="mt-1 overscroll-contain sm:max-h-[min(70vh,640px)] sm:overflow-y-auto">
        {visible.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500 sm:p-6">
            {tab === "aktivní" && "Žádné aktivní notifikace."}
            {tab === "odloženo" && "Žádné odložené notifikace."}
            {tab === "archivováno" && "Archiv je prázdný."}
          </div>
        ) : visible.map((n) => (
          <MiniNotificationItem key={n.id} n={n} tab={tab} vyber={vyber && lzeVybirat}
            onArchive={archive} onSnooze={snooze} onRestore={restore}
            selected={selectedIds.has(n.id)} onToggleSelect={toggleSelect} onOtevrit={otevrit} />
        ))}
      </div>
    </NotifPanel>
  );
}

// Owner náhled "zobrazit jako role" — přepne FRONTEND (menu + viditelné sekce) do dané role.
// Backend API stále běží pod reálnou rolí ownera, takže data jsou owner-level.
const VIEW_AS_OPTIONS = [
  { value: "owner", label: "Owner (já)" },
  { value: "manager", label: "Manager" },
  { value: "sales", label: "Sales" },
  { value: "csm", label: "CSM" },
  { value: "trener", label: "Trenér" },
  // 16.9.2026: trenér najatý čistě na bootcampy. Vidí Lokality, Klientky
  // bootcampů a svoji fakturaci, nic z koučinku.
  { value: "bc_trener", label: "Trenér bootcampů" },
  { value: "nutritionist", label: "Nutritionist" },
];
function ViewAsSwitcher() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const real = getRealUser();
  // Owner: náhled jako libovolná role. CSM: jen Nutritionist (14.7.2026 —
  // zástup za výživovou specialistku; backend má nutri endpointy pro CSM povolené).
  // Pracovní přepínač: CSM <-> Nutritionist (21.7.2026), manažer <-> Sales (1. 10. 2026,
  // manažer občas navolává a domlouvá zkušební tréninky; backend sales manažera pouští).
  const PREPINACE = {
    csm: { druha: "nutritionist", popis: "CSM", druhyPopis: "Nutritionist", barva: "bg-emerald-600" },
    manager: { druha: "sales", popis: "Manager", druhyPopis: "Sales", barva: "bg-emerald-600" },
  };
  const prepinac = real ? PREPINACE[real.role] : null;
  const jeCsm = real && real.role === "csm";
  if (!real || (real.role !== "owner" && !prepinac)) return null;
  const options = jeCsm
    ? [{ value: "csm", label: "CSM (já)" }, { value: "nutritionist", label: "Nutritionist" }]
    : VIEW_AS_OPTIONS;
  const vlastniRole = prepinac ? real.role : "owner";
  const current = getViewAsRole();
  const apply = (val) => {
    setViewAsRole(val);
    const home = val === "bc_trener" ? "/bootcampy"
      : val === "trener" ? "/trener/dashboard" : val === "manager" ? "/manager/dashboard" : val === "nutritionist" ? "/nutri/dashboard" : val === "csm" ? "/csm/dashboard" : val === "sales" ? "/sales/dnes" : "/dashboard";
    window.location.href = home;
  };
  // CSM (21.7.2026): skutecny PREPINAC pracovni role, ne "nahled". CSM <-> Nutritionist,
  // projevi se okamzite (reload do dashboardu role). Backend pousti CSM na nutri endpointy,
  // takze v roli Nutritionist realne pracuje (nejde o read-only nahled jako u ownera).
  // 30. 10. 2026 (hlášení #186, schválený mockup v4): manažer, který sám trénuje
  // (napojený trenér v Nastavení, dnes Michal), má tři role: Manager, Sales, Trenér.
  // Na počítači tři tlačítka vedle sebe, na telefonu ikona jako ostatní v liště
  // a po klepnutí větší výběr role přes celou šířku.
  if (prepinac && real.role === "manager" && real.linkedTrener) {
    const ROLE3 = [
      { value: "manager", popis: "Manager", detail: "Manažerský přehled a úkoly", aktivni: "bg-brand-600", tecka: "bg-brand-600" },
      { value: "sales", popis: "Sales", detail: "Leady a zkušební tréninky", aktivni: "bg-emerald-600", tecka: "bg-emerald-600" },
      { value: "trener", popis: "Trenér", detail: "Moje klientky, kalendář, úkoly", aktivni: "bg-amber-600", tecka: "bg-amber-600" },
    ];
    const aktualni = ROLE3.some((r) => r.value === current) ? current : "manager";
    return (
      <>
      <div className="relative sm:hidden" ref={ref}>
        <button type="button" onClick={() => setOpen((o) => !o)}
          className={`flex h-10 w-10 items-center justify-center rounded-xl ${open ? "bg-slate-100" : ""}`}
          title="Pracovní role" aria-label="Pracovní role" aria-expanded={open}>
          <UserCog className="h-6 w-6 text-slate-600" />
        </button>
        {open && (
          <div className="fixed inset-x-2 top-[64px] z-50 rounded-2xl border border-slate-200 bg-white py-1.5 shadow-xl">
            <div className="px-4 pb-1.5 pt-2.5 text-xs font-bold uppercase tracking-wider text-slate-400">Pracovní role</div>
            {ROLE3.map((r) => {
              const akt = r.value === aktualni;
              return (
                <button key={r.value} type="button" onClick={() => { setOpen(false); if (!akt) apply(r.value); }}
                  className={`mx-1.5 flex min-h-[64px] w-[calc(100%-0.75rem)] items-center justify-between gap-3 rounded-xl px-4 text-left ${akt ? "bg-brand-light" : "active:bg-slate-50"}`}>
                  <span className="flex min-w-0 items-center gap-3.5">
                    <span className={`h-3.5 w-3.5 shrink-0 rounded-full ${r.tecka}`} />
                    <span className="min-w-0">
                      <span className={`block text-lg ${akt ? "font-bold text-brand-950" : "font-semibold text-slate-700"}`}>{r.popis}</span>
                      <span className="block text-[13px] text-slate-500">{r.detail}</span>
                    </span>
                  </span>
                  {akt && <Check className="h-6 w-6 shrink-0 text-brand-deep" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="hidden sm:inline-flex items-center rounded-xl border border-slate-300 overflow-hidden text-xs font-semibold shadow-sm" title="Přepni pracovní roli, projeví se okamžitě">
        {ROLE3.map((r) => (
          <button key={r.value} type="button" onClick={() => { if (r.value !== aktualni) apply(r.value); }}
            className={`px-3 py-1.5 transition-colors ${r.value === aktualni ? `${r.aktivni} text-white` : "bg-white text-slate-600 hover:bg-slate-50"}`}>{r.popis}</button>
        ))}
      </div>
      </>
    );
  }
  if (prepinac) {
    const vDruhe = current === prepinac.druha;
    // 25. 10. 2026 (Michal): na telefonu se lišta nevešla a stopky překrývaly značku vlevo.
    // Na úzké obrazovce je proto přepínač jedno tlačítko s aktuální rolí, klepnutí přepne na druhou.
    const aktivniPopis = vDruhe ? prepinac.druhyPopis : prepinac.popis;
    return (
      <>
      <button type="button" onClick={() => apply(vDruhe ? real.role : prepinac.druha)}
        className={`inline-flex h-10 items-center gap-1 rounded-xl px-2 text-xs font-semibold text-white shadow-sm sm:hidden ${vDruhe ? prepinac.barva : "bg-brand-600"}`}
        title={`Pracovní role ${aktivniPopis}, klepnutím přepneš na ${vDruhe ? prepinac.popis : prepinac.druhyPopis}`}
        aria-label={`Role ${aktivniPopis}, přepnout na ${vDruhe ? prepinac.popis : prepinac.druhyPopis}`}>
        <ArrowLeftRight className="h-3.5 w-3.5 shrink-0" /><span className="[@media(max-width:359px)]:hidden">{aktivniPopis}</span>
      </button>
      <div className="hidden sm:inline-flex items-center rounded-xl border border-slate-300 overflow-hidden text-xs font-semibold shadow-sm" title="Prepni pracovni roli, projevi se okamzite">
        <button type="button" onClick={() => apply(real.role)}
          className={`px-3 py-1.5 transition-colors ${!vDruhe ? "bg-brand-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{prepinac.popis}</button>
        <button type="button" onClick={() => apply(prepinac.druha)}
          className={`px-3 py-1.5 transition-colors ${vDruhe ? `${prepinac.barva} text-white` : "bg-white text-slate-600 hover:bg-slate-50"}`}>{prepinac.druhyPopis}</button>
      </div>
      </>
    );
  }
  return (
    <div className="relative" ref={ref}>
      {/* 12.9.2026: byl to žlutý orámovaný čip, v liště křičel nejvíc, i když
          se používá nejmíň. Teď je to ikona jako ostatní; že je náhled zapnutý,
          hlásí fialový proužek pod lištou (viz PruhNahledu). */}
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl transition-colors ${
          current ? "bg-brand-light text-brand-deep" : "text-slate-600 sm:hover:bg-slate-100"
        }`}
        title={current ? `Náhled jako ${ROLE_LABEL[current] || current}` : "Zobrazit appku jako jiná role"}
        aria-label={current ? `Náhled jako ${ROLE_LABEL[current] || current}` : "Zobrazit jako jiná role"}
      >
        <Eye className="h-6 w-6" />
      </button>
      {open && (
        // 12.9.2026: řádky byly 30 px vysoké, na telefonu se do nich špatně
        // trefovalo. Teď mají 48 px a vybraná role má i podbarvení.
        <div className="absolute right-0 z-30 mt-1 w-60 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg sm:w-52">
          <div className="px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-400 sm:px-3 sm:py-1.5">Zobrazit jako</div>
          {options.map((o) => {
            const active = (o.value === vlastniRole && !current) || o.value === current;
            return (
              <button key={o.value} onClick={() => apply(o.value)}
                className={`flex min-h-[48px] w-full items-center justify-between gap-2 px-4 text-left text-base sm:min-h-[36px] sm:px-3 sm:text-sm ${
                  active ? "bg-brand-light font-bold text-brand-950" : "text-slate-700 sm:hover:bg-slate-50"
                }`}>
                <span className="min-w-0 truncate">{o.label}</span>
                {active && <Check className="h-5 w-5 shrink-0 text-brand-deep sm:h-4 sm:w-4" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function TopBar({ setSidebarOpen, menuSkryte = false, setMenuSkryte }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { alertCount } = useAlertCount();
  const { refresh } = useNotifications();
  const pageTitle = titleFor(location.pathname, String(getCurrentUser()?.role || "").toLowerCase());
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const bellRef = useRef(null);

  // Aktuálně přihlášený uživatel — určuje co se v hlavičce zobrazí
  const user = getCurrentUser();
  const role = user?.role || "";
  const [nahledTrener] = useNahledTrenera();
  // Bell zobrazujeme jen rolím co mají oprávnění vidět owner notifikace.
  // Trenér je nemá vidět — má své vlastní upozornění v trener portálu (Hero box, Pozornost sekce).
  // 4.9.2026: owner uvnitř trenérského portálu má v liště výběr trenéra a
  // trenérský zvoneček. Vlastní owner zvoneček by tam byl třetí ikona navíc.
  const realRole = user?._realRole || role;
  const jeOwnerVPortalu = realRole === "owner" && String(location.pathname).startsWith("/trener/");
  const showBell = ROLES_S_NOTIFIKACEMI.has(role) && !jeOwnerVPortalu;
  const roleLabel = ROLE_LABEL[role] || (role ? role[0].toUpperCase() + role.slice(1) : "");
  const roleBadgeStyle = ROLE_BADGE_STYLE[role] || "bg-slate-100 text-slate-700 border-slate-300";

  // Otevření dropdownu triggernue čerstvý refresh notifikací z backendu —
  // bez toho může alertCount a list notifikací rozjet po archive/restore/milník replay.
  const handleBellToggle = () => {
    setDropdownOpen((o) => {
      if (!o) refresh();
      return !o;
    });
  };

  // Zavři dropdown po kliku mimo
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => {
      // Panel je na mobilu portalovany do <body>, takze neni potomkem bellRef.
      if (e.target.closest && e.target.closest("[data-notif-panel]")) return;
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  // 2.9.2026: hamburger se zrušil úplně. Na telefonu se sekce přepínají spodní
  // lištou (layout/SpodniLista.jsx), na počítači je postranní panel vidět vždy.
  const isTrener = role === "trener";
  // V trenérském portálu ukazujeme jméno trenéra, jinak přihlášeného člověka.
  const jmenoVListe = (isTrener ? (user?.linkedTrener || user?.name) : user?.name) || "FitWay40 admin";
  const isManager = role === "manager";
  const isCsm = role === "csm";
  // 30. 10. 2026 (#186): manažer, který trénuje, má jeden společný zvoneček
  // (manažerské i trenérské notifikace) v roli Manager i v roli Trenér.
  const jeSjednocenyZvonek = realRole === "manager" && !!user?.linkedTrener && (isManager || isTrener);

  return (
    <>
    <div className="flex min-h-[60px] items-center justify-between gap-2 sm:gap-3 border-b border-brand/10 bg-white/85 px-4 py-2 shadow-[0_10px_18px_-14px_rgba(36,20,48,0.25)] backdrop-blur-md lg:min-h-[64px] lg:px-6 lg:py-2.5">
      <div className={`flex items-center gap-3 min-w-0 ${jeOwnerVPortalu ? "flex-1" : ""}`}>
        {/* Schovat nebo vrátit menu. Jen na počítači, na telefonu je zásuvka. */}
        {setMenuSkryte && (
          <button
            className={`${isTrener ? "hidden" : "hidden lg:flex"} h-11 w-11 items-center justify-center rounded-xl hover:bg-brand-light`}
            onClick={() => setMenuSkryte(!menuSkryte)}
            aria-label={menuSkryte ? "Zobrazit menu" : "Schovat menu"}
            title={menuSkryte ? "Zobrazit menu" : "Schovat menu, práce přes celou obrazovku"}
            data-prepnout-menu
          >
            {menuSkryte ? <PanelLeftOpen className="h-6 w-6 text-slate-600" /> : <PanelLeftClose className="h-6 w-6 text-slate-600" />}
          </button>
        )}
        {/* 4.9.2026: pruh se jménem a fotkou uvnitř trenérského portálu je pryč,
            jméno a fotka jsou tady. Pod jménem je název stránky. */}
        {jeOwnerVPortalu ? (
          <VyberTrenera podtitulek={pageTitle} />
        ) : (
          <>
            {/* 12.9.2026: na telefonu je vlevo jen značka v kolečku. Fotka
                a jméno tam nic neřešily, vlastní jméno člověk zná. Na počítači
                je místa dost, tam fotka i jméno zůstávají. */}
            <ZnackaVListe />
            <MenuUJmena foto={user?.picture} jmeno={jmenoVListe} />
          </>
        )}
      </div>
      <div className="flex flex-shrink-0 items-center gap-0.5 sm:gap-1">
        {/* Zvoneček s notifikacemi — jen pro role co mají oprávnění je vidět.
            Trenér NEMÁ vidět owner notifikace (undecided leads, ROI, CPL atp.).
            Pro trener má své vlastní upozornění uvnitř trener portálu. */}
        {/* Stopky (Toggl styl) — měřicí role (manager/csm/sales/nutritionist), vedle zvonečku.
            Owner a trenér neměří, widget se jim nerenderuje (řeší si sám dle role). */}
        <TimerWidget />
        {/* Nová záložka uvnitř adminu. Nechá otevřenou stránku (detail klienta,
            úkol) tam, kde je, a otevře vedle ní prázdnou. Na telefonu nemá
            smysl, řada záložek se tam nekreslí. Sales ji nemá (1. 10. 2026,
            mockup „Rozhraní Miluše“), pletla by se s taby Dnes, Kontakty, Zkušební. */}
        {user?.role !== "sales" && <button
          className="hidden h-11 w-11 items-center justify-center rounded-xl hover:bg-slate-100 lg:flex"
          aria-label="Nová záložka"
          title="Nová záložka (tahle stránka zůstane otevřená)"
          data-nova-zalozka
          onClick={() => window.dispatchEvent(new CustomEvent(UDALOST_ZALOZKA, {
            detail: { to: defaultPathForRole(getCurrentUser()?.role), title: "Přehled" },
          }))}
        >
          <Copy className="h-6 w-6 text-slate-600" />
        </button>}

        {/* Nápověda trenéra (AI z aktuálního manuálu), vedle hlášení bugů. Trenér, head coach, manažer, owner. */}
        {["trener", "head_coach", "manager", "owner"].includes(String(role).toLowerCase()) && <NapovedaTrenera />}

        {/* Feedback tlacitko — pred zvonkem, dostupne vsem prihlasenym (i trenerum) */}
        <button
          className="flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl hover:bg-slate-100"
          aria-label="Nahlásit / Zpětná vazba"
          title="Nahlásit / Zpětná vazba"
          onClick={() => setFeedbackOpen(true)}
        >
          <MessageSquare className="h-6 w-6 text-slate-600" />
        </button>
        {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}

        {/* Trenér: vlastní zvoneček (úkoly, neaktivní klienti, dokumenty, končící spolupráce)
            přímo v horní liště, aby ho měl po ruce hned po přihlášení. */}
        {(isTrener || jeOwnerVPortalu) && !jeSjednocenyZvonek && (
          <TrenerBell
            activeTrener={jeOwnerVPortalu ? nahledTrener : (user?.linkedTrener || user?.name || "")}
            variant="light"
          />
        )}

        {/* Manažer a CSM: vlastní zvoneček (nové úkoly, po deadlinu, ke kontrole;
            manager navíc trenérské úkoly po termínu), ne owner metriky. */}
        {(isManager || isCsm || jeSjednocenyZvonek) ? (
          <ManagerBell />
        ) : showBell && (
          <div className="relative" ref={bellRef}>
            <button
              className="relative flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl hover:bg-slate-100"
              aria-label="Notifikace"
              onClick={handleBellToggle}
            >
              <Bell className={`h-6 w-6 ${alertCount > 0 ? "text-red-600" : "text-slate-600"}`} />
              {alertCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold leading-none px-1">
                  {alertCount}
                </span>
              )}
            </button>
            {dropdownOpen && <BellDropdown onClose={() => setDropdownOpen(false)} />}
          </div>
        )}
        {/* Role badge — dynamicky podle role aktuálně přihlášeného uživatele */}
        <ViewAsSwitcher />
        {roleLabel && (
          <div className={`px-3 py-1.5 rounded-xl text-xs font-medium border hidden sm:block ${roleBadgeStyle}`}>
            {roleLabel}
          </div>
        )}
      </div>
    </div>
    <PruhNahledu />
    </>
  );
}

/**
 * Značka v kolečku. Na telefonu stojí vlevo v liště místo fotky a jména.
 * Skládá se z bílé ikony na značkové fialové, tedy z toho, co už je v public,
 * takže nepřibývá další obrázek a je ostrá na jakékoli velikosti.
 */
function ZnackaVListe() {
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand lg:hidden">
      <img src="/ikona-bila.svg" alt="FitWay40" className="h-6 w-auto" />
    </span>
  );
}

/**
 * Proužek pod lištou, když se owner dívá za jinou roli. 12.9.2026: dřív to
 * hlásil jen žlutý čip v liště, kterého si člověk po chvíli přestal všímat
 * a pak se divil, proč vidí cizí data. Proužek je vidět pořád a tlačítko
 * Zpět vrátí vlastní pohled jedním klepnutím.
 */
function PruhNahledu() {
  const real = getRealUser();
  const current = getViewAsRole();
  // CSM přepínač rolí není náhled, ten se tímhle nehlásí.
  if (!real || real.role !== "owner" || !current) return null;
  return (
    <div className="flex items-center gap-2 border-b border-brand/10 bg-brand-light px-4 py-2 lg:px-6">
      <Eye className="h-4 w-4 shrink-0 text-brand-deep" />
      <span className="min-w-0 flex-1 truncate text-xs font-bold uppercase tracking-wide text-brand-950">
        Náhled jako {ROLE_LABEL[current] || current}
      </span>
      <button
        type="button"
        onClick={() => { setViewAsRole(""); window.location.href = defaultPathForRole(real.role); }}
        className="flex h-10 shrink-0 items-center rounded-xl bg-white px-4 text-sm font-bold text-brand-deep shadow-sm transition active:scale-95 sm:hover:bg-brand-50"
      >
        Zpět
      </button>
    </div>
  );
}

// Fotka nebo iniciálky do horní lišty. Bez fotky se poskládají iniciálky.
function AvatarUzivatele({ foto, jmeno }) {
  const iniciály = String(jmeno || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((c) => c[0])
    .join("")
    .toUpperCase();
  if (foto) {
    return <img src={foto} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />;
  }
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-200 text-sm font-bold text-brand-900">
      {iniciály || "FW"}
    </span>
  );
}

/**
 * Jméno s fotkou vlevo nahoře (jen počítač) jako tlačítko s menu. 21.9.2026:
 * první položka je propojení Slacku pro status 🌴 při dovolené, pod ní
 * Odhlásit. Na telefonu je totéž ve spodní liště pod Více.
 */
function MenuUJmena({ foto, jmeno }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      // Potvrzovací dialog odpojení je mimo menu, klik do něj menu nezavírá.
      if (e.target.closest && e.target.closest("[role=dialog]")) return;
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  return (
    <div className="relative hidden lg:block lg:min-w-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex min-w-0 items-center gap-3 rounded-xl py-1 pl-1 pr-2 transition hover:bg-slate-100"
        title="Můj účet"
      >
        <AvatarUzivatele foto={foto} jmeno={jmeno} />
        <span className="min-w-0 truncate text-xl font-bold leading-tight text-slate-900 demo-sensitive">{jmeno}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 z-30 mt-2 w-72 space-y-1 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
          <button
            type="button"
            onClick={() => { setOpen(false); navigate("/muj-ucet"); }}
            className="flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <UserCog className="h-4 w-4 shrink-0 text-slate-500" /> Můj účet
          </button>
          <button
            type="button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 text-sm font-semibold text-rose-700 hover:bg-rose-50"
          >
            <LogOut className="h-4 w-4 shrink-0" /> Odhlásit
          </button>
        </div>
      )}
    </div>
  );
}
